// RITA-1525 (Teilpaket 2) — Werkzeugleiste des Vertragseditors (Reihenfolge wie in der
// freigegebenen Skizze): Rückgängig/Wiederholen, Fett/Kursiv/Unterstrichen, Schriftgröße (RITA-1534,
// Auswahlfeld in `Schriftgroessenwahl.tsx`), Zeilenabstand und Abstand nach Absatz (RITA-1571,
// `Abstandswahl.tsx`), Aufzählung, Kästchen, Seitenumbruch, Kopf-/Fußzeile, Logo
// und Seitenzahl. Die Knöpfe verhindern `mousedown`, damit die Auswahl im Editor erhalten bleibt; die
// Einstellungen zu Logo und Seitenzahl erscheinen, sobald Kopf- bzw. Fußzeile bearbeitet werden. Das
// Logo wird seit RITA-1526 über die Vertragsart gewählt (Feld „Logo (Vertragsart)“, Entscheidung Julia
// 28.09.2026); Vertragsarten ohne Logo bleiben wählbar und werden mit Hinweis angezeigt.
import React from 'react';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import InsertPageBreakOutlinedIcon from '@mui/icons-material/InsertPageBreakOutlined';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Seiteninhalt } from '../layout/seitenAufteilen';
import { Bereich } from '../model/positionen';
import { HorizontalePosition } from '../model/types';
import { VertragEditorApi } from '../editor/useVertragEditor';
import Abstandswahl, { Abstandsgeltung } from './Abstandswahl';
import Schriftgroessenwahl from './Schriftgroessenwahl';
import {
  LOGO_FEHLT_HINWEIS,
  LOGO_FELDNAME,
  LOGO_HILFETEXT,
  VERTRAGSART_UNBEKANNT_HINWEIS,
  VERTRAGSART_GESPEICHERT_NICHT_LADBAR,
  VERTRAGSARTEN_FEHLER_TEXT,
  VERTRAGSARTEN_LADEN_TEXT,
  useVertragsarten,
  vertragsartFinden,
} from './VertragsartenKontext';

interface Props {
  editor: VertragEditorApi;
  seiten: Seiteninhalt[];
  gesperrt?: boolean;
  onVertragsartenErneutLaden?: () => void;
  /** Wofür Zeilenabstand und Abstand nach Absatz gelten (Hinweis beim Darüberfahren, RITA-1571). */
  geltung?: Abstandsgeltung;
}

const POSITIONEN: ReadonlyArray<{ wert: HorizontalePosition; bezeichnung: string }> = [
  { wert: 'links', bezeichnung: 'links' },
  { wert: 'mitte', bezeichnung: 'mitte' },
  { wert: 'rechts', bezeichnung: 'rechts' },
];

const LOGOHOEHEN = [10, 12, 15, 20];

const BEREICHSNAMEN: Record<Bereich, string> = { text: 'Text', kopf: 'Kopfzeile', fuss: 'Fußzeile' };

const fokusHalten = (event: React.MouseEvent) => event.preventDefault();

interface KnopfProps {
  titel: string;
  aktiv?: boolean;
  deaktiviert?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function Knopf({ titel, aktiv = false, deaktiviert = false, onClick, children }: KnopfProps) {
  return (
    <Tooltip title={titel}>
      <span>
        <IconButton
          size="small"
          aria-label={titel}
          aria-pressed={aktiv}
          color={aktiv ? 'primary' : 'default'}
          disabled={deaktiviert}
          onMouseDown={fokusHalten}
          onClick={onClick}
          sx={aktiv ? { bgcolor: 'action.selected' } : undefined}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function caretSeite(editor: VertragEditorApi, seiten: Seiteninhalt[]): number | null {
  const { auswahl, bereich, aktiveSeite } = editor.zustand;
  if (bereich !== 'text') return aktiveSeite;
  if (!auswahl) return null;
  const index = seiten.findIndex((seite) => seite.blockIds.includes(auswahl.fokus.blockId));
  return index >= 0 ? index + 1 : null;
}

function Werkzeugleiste({ editor, seiten, gesperrt = false, onVertragsartenErneutLaden, geltung = 'vorlage' }: Props) {
  const { zustand, formatAktuell, kannRueckgaengig, kannWiederholen } = editor;
  // Vor jedem Befehl die DOM-Auswahl ins Modell übernehmen (selectionchange kommt asynchron).
  const befehl =
    <A extends unknown[]>(ausfuehren: (...argumente: A) => void) =>
    (...argumente: A) => {
      if (gesperrt) return;
      editor.auswahlLeser.current?.();
      ausfuehren(...argumente);
    };
  const befehle = {
    rueckgaengig: befehl(editor.befehle.rueckgaengig),
    wiederholen: befehl(editor.befehle.wiederholen),
    format: befehl(editor.befehle.format),
    liste: befehl(editor.befehle.liste),
    kaestchenEinfuegen: befehl(editor.befehle.kaestchenEinfuegen),
    seitenumbruch: befehl(editor.befehle.seitenumbruch),
    bereichWechseln: befehl(editor.befehle.bereichWechseln),
    logo: (wert: Parameters<typeof editor.befehle.logo>[0]) => {
      if (!gesperrt) editor.befehle.logo(wert);
    },
    seitenzahl: (wert: Parameters<typeof editor.befehle.seitenzahl>[0]) => {
      if (!gesperrt) editor.befehle.seitenzahl(wert);
    },
  };
  const { bereich } = zustand;
  const { logo } = zustand.dokument.kopf;
  const { seitenzahl } = zustand.dokument.fuss;
  const seite = caretSeite(editor, seiten);
  // Logo über die Vertragsart: Hilfetext unter dem Feld, Hinweis in eigener Zeile darunter, wenn die
  // gewählte Vertragsart kein Logo (mehr) hat oder gelöscht wurde. Während des Ladens und bei einem
  // Ladefehler ist die Auswahl gesperrt und eine gesetzte id gilt nicht als „gelöscht“: Die Liste
  // fehlt dann oder zeigt nur den zuletzt geladenen Stand (Apollo-Cache schon gefüllt).
  const vertragsarten = useVertragsarten();
  const gewaehlteVertragsart = vertragsartFinden(vertragsarten, logo.vertragsartId);
  const vertragsartUnbekannt =
    logo.vertragsartId !== null && !gewaehlteVertragsart && !vertragsarten.laedt && !vertragsarten.fehler;
  const gespeicherteVertragsartNichtLadbar =
    logo.vertragsartId !== null && !gewaehlteVertragsart && vertragsarten.fehler;
  let logoHilfetext = LOGO_HILFETEXT;
  if (vertragsarten.laedt) logoHilfetext = VERTRAGSARTEN_LADEN_TEXT;
  else if (vertragsarten.fehler) logoHilfetext = VERTRAGSARTEN_FEHLER_TEXT;
  let logoHinweis: string | null = null;
  if (gewaehlteVertragsart && !gewaehlteVertragsart.logoDataUrl) logoHinweis = LOGO_FEHLT_HINWEIS;
  else if (vertragsartUnbekannt) logoHinweis = VERTRAGSART_UNBEKANNT_HINWEIS;

  return (
    <Paper variant="outlined" data-testid="vertrag-werkzeugleiste">
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, px: 1, py: 0.5 }}>
        <Knopf titel="Rückgängig" deaktiviert={gesperrt || !kannRueckgaengig} onClick={befehle.rueckgaengig}>
          <UndoIcon fontSize="small" />
        </Knopf>
        <Knopf titel="Wiederholen" deaktiviert={gesperrt || !kannWiederholen} onClick={befehle.wiederholen}>
          <RedoIcon fontSize="small" />
        </Knopf>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Knopf titel="Fett" aktiv={formatAktuell.fett} deaktiviert={gesperrt} onClick={() => befehle.format('fett')}>
          <FormatBoldIcon fontSize="small" />
        </Knopf>
        <Knopf
          titel="Kursiv"
          aktiv={formatAktuell.kursiv}
          deaktiviert={gesperrt}
          onClick={() => befehle.format('kursiv')}
        >
          <FormatItalicIcon fontSize="small" />
        </Knopf>
        <Knopf
          titel="Unterstrichen"
          aktiv={formatAktuell.unterstrichen}
          deaktiviert={gesperrt}
          onClick={() => befehle.format('unterstrichen')}
        >
          <FormatUnderlinedIcon fontSize="small" />
        </Knopf>
        <Schriftgroessenwahl editor={editor} gesperrt={gesperrt} />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Abstandswahl editor={editor} gesperrt={gesperrt} geltung={geltung} />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Knopf titel="Aufzählung" deaktiviert={gesperrt} onClick={befehle.liste}>
          <FormatListBulletedIcon fontSize="small" />
        </Knopf>
        <Knopf titel="Kästchen einfügen" deaktiviert={gesperrt} onClick={befehle.kaestchenEinfuegen}>
          <CheckBoxOutlineBlankIcon fontSize="small" />
        </Knopf>
        <Knopf
          titel="Seitenumbruch einfügen"
          deaktiviert={gesperrt || bereich !== 'text'}
          onClick={befehle.seitenumbruch}
        >
          <InsertPageBreakOutlinedIcon fontSize="small" />
        </Knopf>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Knopf
          titel="Kopfzeile bearbeiten"
          aktiv={bereich === 'kopf'}
          deaktiviert={gesperrt}
          onClick={() => befehle.bereichWechseln(bereich === 'kopf' ? 'text' : 'kopf', seite ?? undefined)}
        >
          <VerticalAlignTopIcon fontSize="small" />
        </Knopf>
        <Knopf
          titel="Fußzeile bearbeiten"
          aktiv={bereich === 'fuss'}
          deaktiviert={gesperrt}
          onClick={() => befehle.bereichWechseln(bereich === 'fuss' ? 'text' : 'fuss', seite ?? undefined)}
        >
          <VerticalAlignBottomIcon fontSize="small" />
        </Knopf>
        <Typography variant="body2" sx={{ ml: 'auto', color: 'text.secondary' }} data-testid="vertrag-caret-seite">
          {`${BEREICHSNAMEN[bereich]} · ${seite ? `Seite ${seite} von ${seiten.length}` : `${seiten.length} Seiten`}`}
        </Typography>
      </Box>
      {bereich !== 'text' && (
        // Oben ausgerichtet: das Logo-Feld trägt einen Hilfetext und ist dadurch höher als die Nachbarfelder;
        // Beschriftung und „Fertig“ rücken per Abstand auf die Höhe der Eingabefelder.
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 2,
            px: 1.5,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
          }}
          data-testid="vertrag-kopf-fuss-einstellungen"
        >
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1.25 }}>
            {bereich === 'kopf' ? 'Kopfzeile (gilt für alle Seiten)' : 'Fußzeile (gilt für alle Seiten)'}
          </Typography>
          {bereich === 'kopf' && (
            <>
              <TextField
                select
                size="small"
                id="vertrag-logo-auswahl"
                label={LOGO_FELDNAME}
                value={logo.vertragsartId === null ? '' : String(logo.vertragsartId)}
                onChange={(event) =>
                  befehle.logo({ vertragsartId: event.target.value ? Number(event.target.value) : null })
                }
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  'aria-label': LOGO_FELDNAME,
                  // Hilfetext und (falls vorhanden) Hinweis gehören für Vorleseprogramme zur Auswahl.
                  'aria-describedby': logoHinweis
                    ? 'vertrag-logo-auswahl-helper-text vertrag-logo-hinweis'
                    : 'vertrag-logo-auswahl-helper-text',
                }}
                helperText={logoHilfetext}
                disabled={gesperrt || vertragsarten.laedt || vertragsarten.fehler}
                sx={{ minWidth: 240 }}
              >
                {vertragsarten.vertragsarten.map((vertragsart) => (
                  <option key={vertragsart.id} value={String(vertragsart.id)}>
                    {vertragsart.logoDataUrl ? vertragsart.titel : `${vertragsart.titel} (kein Logo)`}
                  </option>
                ))}
                {vertragsartUnbekannt && (
                  <option value={String(logo.vertragsartId)}>{`Unbekannte Vertragsart (${logo.vertragsartId})`}</option>
                )}
                {gespeicherteVertragsartNichtLadbar && (
                  <option value={String(logo.vertragsartId)}>{VERTRAGSART_GESPEICHERT_NICHT_LADBAR}</option>
                )}
                {/* Zuletzt, weil fast alle Verträge ein Logo tragen (Entscheidung Julia 28.09.2026). */}
                <option value="">kein Logo</option>
              </TextField>
              {vertragsarten.fehler && onVertragsartenErneutLaden && (
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  disabled={gesperrt}
                  onClick={() => {
                    if (!gesperrt) onVertragsartenErneutLaden();
                  }}
                  sx={{ mt: 0.5 }}
                >
                  Erneut versuchen
                </Button>
              )}
              {logoHinweis && (
                // Eigene volle Zeile unter den Feldern, damit der Hinweis nicht zwischen die Auswahlfelder rutscht.
                <Typography
                  variant="body2"
                  id="vertrag-logo-hinweis"
                  role="status"
                  sx={{ color: 'warning.main', flexBasis: '100%', order: 1 }}
                  data-testid="vertrag-logo-hinweis"
                >
                  {logoHinweis}
                </Typography>
              )}
              <TextField
                select
                size="small"
                label="Logo-Position"
                disabled={gesperrt}
                value={logo.position}
                onChange={(event) => befehle.logo({ position: event.target.value as HorizontalePosition })}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'aria-label': 'Logo-Position' }}
                sx={{ minWidth: 140 }}
              >
                {POSITIONEN.map((eintrag) => (
                  <option key={eintrag.wert} value={eintrag.wert}>
                    {eintrag.bezeichnung}
                  </option>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Logo-Höhe"
                disabled={gesperrt}
                value={String(logo.hoeheMm)}
                onChange={(event) => befehle.logo({ hoeheMm: Number(event.target.value) })}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'aria-label': 'Logo-Höhe' }}
                sx={{ minWidth: 120 }}
              >
                {(LOGOHOEHEN.includes(logo.hoeheMm) ? LOGOHOEHEN : [...LOGOHOEHEN, logo.hoeheMm]).map((hoehe) => (
                  <option key={hoehe} value={String(hoehe)}>
                    {`${hoehe} mm`}
                  </option>
                ))}
              </TextField>
            </>
          )}
          {bereich === 'fuss' && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={seitenzahl.anzeigen}
                    disabled={gesperrt}
                    onChange={(event) => befehle.seitenzahl({ anzeigen: event.target.checked })}
                    inputProps={{ 'aria-label': 'Seitenzahl' }}
                  />
                }
                label="Seitenzahl"
              />
              <TextField
                select
                size="small"
                label="Seitenzahl-Position"
                disabled={gesperrt}
                value={seitenzahl.position}
                onChange={(event) => befehle.seitenzahl({ position: event.target.value as HorizontalePosition })}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'aria-label': 'Seitenzahl-Position' }}
                sx={{ minWidth: 160 }}
              >
                {POSITIONEN.map((eintrag) => (
                  <option key={eintrag.wert} value={eintrag.wert}>
                    {eintrag.bezeichnung}
                  </option>
                ))}
              </TextField>
            </>
          )}
          <Button
            size="small"
            variant="outlined"
            disabled={gesperrt}
            sx={{ ml: 'auto', mt: 0.5 }}
            onClick={() => befehle.bereichWechseln('text')}
          >
            Fertig
          </Button>
        </Box>
      )}
    </Paper>
  );
}

export default Werkzeugleiste;
